/******************************************************************************

Copyright (c) 2016-2018, Highsoft

Permission is hereby granted, free of charge, to any person obtaining
a copy of this software and associated documentation files (the
"Software"), to deal in the Software without restriction, including
without limitation the rights to use, copy, modify, merge, publish,
distribute, sublicense, and/or sell copies of the Software, and to
permit persons to whom the Software is furnished to do so, subject to
the following conditions:

The above copyright notice and this permission notice shall be
included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT.
IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY
CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT,
TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.

******************************************************************************/

// @format

import { ready, isFn } from './../../core/highcharts-editor';
import { cr, ap, setStyle, on, getElementSize } from './../../core/highed.dom';
import dimmer from './../highed.dimmer';
import fontAwesomeIcons from './../../meta/highed.meta.fontawesome';
import emojiIcons from './../../meta/highed.meta.emojis';

let container = cr('div', 'highed-colorpicker highed-colorpicker-responsive'),
  icons = {
    emojis: emojiIcons,
    fontAwesomeIcons: fontAwesomeIcons
  },
  iconsContainer = cr('div', 'highed-icon-picker-container'),
  toggleContainer = cr('div', 'highed-icon-picker-toggle-container'),
  fontAwesomeToggle = cr('div', 'highed-icon-picker-toggle fontawesome active', '<i class="fa fa-smile-o">'),
  searchBar = cr('input', 'highed-icon-picker-search'),
  emojiToggle = cr('div', 'highed-icon-picker-toggle', '😀'),
  fontAwesomeKeys = Object.keys(fontAwesomeIcons || {}),
  emojiKeys = Object.keys(emojiIcons || {}),
  dbinder = false,
  activeIconList = 'fontAwesome';

//Attach the container to the document when the document is ready
ready(function () {
  ap(document.body, container);
});

ap(container, ap(toggleContainer, fontAwesomeToggle, emojiToggle), searchBar, iconsContainer);

/** Icon picker
 *  Component to pick icons either from fontawesome or emojis
 *
 *  emojis defined in meta/highed.meta.emojis.js,
 *  fontawesome defined in meta/highed.meta.fontawesome.js
 *
 *
 *  @param x {number} - the x position to display the picker at
 *  @param y {number} - the y position to display the picker at
 *  @param current {string} - the current icon
 *  @param fn {function} - the function to call when the icon changes
 *    > newIcon {string} - the color selected by the user
 */
export default function (x, y, fn, props) {
  if (!props) props = {};

  let windowSize = getElementSize(document.body),
    containerSize = getElementSize(container),
    faBinders = [],
    searchBarEvent = false,
    emojiBinders = [],
    faToggleEvent,
    emojiToggleEvent;

  ///////////////////////////////////////////////////////////////////////

  function removeAllEvents() {
    removeFAEvents();
    removeEmojiEvents();
    if (searchBarEvent) searchBarEvent();
  }

  function removeFAEvents() {
    for (let i = faBinders.length - 1; i >= 0; i--) {
      faBinders[i]();
      faBinders.splice(i, 1);
    }
  }
  function removeEmojiEvents() {
    for (let i = emojiBinders.length - 1; i >= 0; i--) {
      emojiBinders[i]();
      emojiBinders.splice(i, 1);
    }
  }

  function drawEmojis() {
    emojiKeys = Object.keys(emojiIcons || {});
    removeEmojiEvents();
    searchBarEvent = on(searchBar, 'keyup', function () {
      drawActiveIconListSearch(searchBar.value);
    });
    emojiKeys.forEach(function (key) {
      drawEmojiIcon(key);
    });
  }

  function drawFontAwesome() {
    removeFAEvents();
    searchBarEvent = on(searchBar, 'keyup', function () {
      drawActiveIconListSearch(searchBar.value);
    });
    fontAwesomeKeys = Object.keys(fontAwesomeIcons || {});
    fontAwesomeKeys.forEach(function (key) {
      drawFontAwesomeIcon(key);
    });
  }

  function drawIcon(key, type, arr) {
    let icon = cr('div', 'highed-icon-container', icons[type][key].unicode);
    icon.title = key.replace(/-o/g, '').replace(/-/g, ' ');
    ap(iconsContainer, icon);
    arr.push(
      on(icon, 'click', function (event) {
        pickActiveIcon(event, key, icons[type][key]);
      })
    );
  }

  function drawEmojiIcon(key) {
    drawIcon(key, 'emojis', emojiBinders);
  }
  function drawFontAwesomeIcon(key) {
    drawIcon(key, 'fontAwesomeIcons', faBinders);
  }

  /* Draws the color picker */
  function drawActiveIconList() {
    if (searchBar.value !== '') {
      drawActiveIconListSearch(searchBar.value);
      return;
    }
    iconsContainer.innerHTML = '';
    if (activeIconList === 'fontAwesome') drawFontAwesome();
    else drawEmojis();
  }

  function drawActiveIconListSearch(text) {
    if (text === '') {
      drawActiveIconList();
      return;
    }

    iconsContainer.innerHTML = '';
    text = text.toLowerCase();
    if (activeIconList === 'fontAwesome') {
      removeFAEvents();
      fontAwesomeKeys = Object.keys(fontAwesomeIcons || {});
      fontAwesomeKeys.forEach(function (key) {
        if (key.indexOf(text) > -1) {
          drawFontAwesomeIcon(key);
        }
      });
    } else {
      emojiKeys = Object.keys(emojiIcons || {});
      removeEmojiEvents();
      emojiKeys.forEach(function (key) {
        if (key.indexOf(text) > -1) {
          drawEmojiIcon(key);
        }
      });
    }
  }

  /* Hide the picker */
  function hide() {
    setStyle(container, {
      opacity: 0,
      left: '-20000px',
      'pointer-events': 'none'
    });
    if (emojiToggleEvent) {
      emojiToggleEvent();
    }
    if (faToggleEvent) {
      faToggleEvent();
    }
    searchBar.value = '';
    removeAllEvents();
    if (dbinder) dbinder();
  }

  function pickActiveIcon(e, key, icon) {
    hide();
    if (isFn(fn)) {
      fn(escape(icon.unicode || icon), icon.unicode);
    }

    e.cancelBubble = true;
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    return false;
  }

  ///////////////////////////////////////////////////////////////////////

  ap(document.body, container);
  //Make sure we're not off screen
  if (x > windowSize.w - containerSize.w) {
    x = windowSize.w - containerSize.w - 10;
  }

  if (y > windowSize.h - containerSize.h) {
    y = windowSize.h - containerSize.h - 10;
  }

  setStyle(container, {
    left: x + 'px',
    top: y + 'px',
    opacity: 1,
    'pointer-events': 'auto'
  });

  faToggleEvent = on(fontAwesomeToggle, 'click', () => {
    activeIconList = 'fontAwesome';
    emojiToggle.classList.remove('active');
    if (!fontAwesomeToggle.classList.contains('active')) {
      fontAwesomeToggle.className += ' active';
      drawActiveIconList();
    }
  });

  emojiToggleEvent = on(emojiToggle, 'click', () => {
    activeIconList = 'emoji';
    fontAwesomeToggle.classList.remove('active');
    if (!emojiToggle.classList.contains('active')) {
      emojiToggle.className += ' active';
      drawActiveIconList();
    }
  });

  dbinder = dimmer(hide, true, true, 5);

  drawActiveIconList();

  ///////////////////////////////////////////////////////////////////////

  return {
    hide: hide
  };
}
